import {
  JobRFQDraftOrgItemsViewer,
  JobShippingSpec,
} from 'domains/jobconfigDomain';
import {
  PurchaseOrderJobShippingItem,
  ShippingInfoAddress,
} from 'domains/orderDomain';
import { ContactAddress } from 'domains/orgDomain';
import { compact } from 'lodash-es';

/**
 * Whether an address in this country is described by US state codes.
 *
 * Accepts both the ISO-2 code used across the platform and the "USA" spelling, since the org
 * address country is free text. Mirrors StatesCodeUtils.isUnitedStates on the backend.
 */
export const isUnitedStates = (countryCode?: string | null) => {
  const code = countryCode?.trim().toUpperCase();
  return code === 'US' || code === 'USA';
};

export const addressTranslator = {
  addressToData: (addressString: string) => {
    const addressArray = addressString.split(', ');
    return {
      toStateProvinceCode: addressArray[0] || '',
      toPostalCode: addressArray[1] || '',
      toCity: addressArray[2] || '',
      toCountryCode: addressArray[3] || '',
      toAddressLine: addressArray[4] || '',
    };
  },
  dataToAddress: (
    addressData: Partial<PurchaseOrderJobShippingItem['shippingInfo']>
  ) => {
    const addressArr = [
      addressData.toAddressLine,
      addressData.toCity,
      addressData.toStateProvinceCode,
      addressData.toPostalCode,
      addressData.toCountryCode,
    ];

    return addressArr.filter((item) => item).join(', ');
  },
  rfqDetermineShippingDataToAddress: (
    addressData: Partial<JobRFQDraftOrgItemsViewer>
  ) => {
    const addressArr = [
      addressData.fromAddressLine,
      addressData.fromCity,
      addressData.fromStateProvinceCode,
      addressData.fromPostalCode,
      addressData.fromCountryCode,
    ];

    return addressArr.filter((item) => item).join(', ');
  },
  contactToAddress: (address: ContactAddress) => {
    // state is optional (many countries have no state/province), and the other
    // parts can be missing on partially filled addresses, so never dereference
    // them directly here
    const stripCommas = (value?: string | null, replacement = ' ') =>
      value ? value.replaceAll(',', replacement) : '';

    return {
      toAddressLine: stripCommas(
        `${address.addressLine1 ?? ''} ${address.addressLine2 ?? ''}`,
        ''
      ),
      toCity: stripCommas(address.city),
      toCountryCode: stripCommas(address.country),
      toStateProvinceCode: stripCommas(address.state),
      toPostalCode: stripCommas(address.postalCode),
    };
  },

  getShippingAddressByContact: (
    address: Partial<ContactAddress>,
    company?: string | null
  ) => {
    return compact([
      company,
      compact([address.addressLine1, address.addressLine2]).join(' '),
      address.city,
      address.state,
      address.postalCode,
      address.country,
    ])
      .filter((item) => item)
      .join(', ');
  },

  getCompactAddress: (address: Partial<ContactAddress>) => {
    return compact([
      compact([address.addressLine1, address.addressLine2]).join(' '),
      address.city,
      address.state,
      address.postalCode,
      address.country,
    ])
      .filter((item) => item)
      .join(', ');
  },

  getAddressLineByJobShippingSpec: (jobShippingSpec: JobShippingSpec) => {
    return addressTranslator.getShippingAddressByContact(
      {
        addressLine1: jobShippingSpec.toAddressLine,
        city: jobShippingSpec.toCity,
        state: jobShippingSpec.toStateProvinceCode,
        postalCode: jobShippingSpec.toPostalCode,
        country: jobShippingSpec.toCountryCode,
      },
      jobShippingSpec.toCompany
    );
  },

  isSameAddrStr: (addr1: string, addr2: string) => {
    if (addr1 === addr2) {
      return true;
    }

    return addr1.replaceAll(', ', ' ') === addr2.replaceAll(', ', ' ');
  },
};

export const addressKeyHandle = (shipping: PurchaseOrderJobShippingItem) => {
  const address = shipping.shippingInfo as ShippingInfoAddress;
  const carrier = shipping.carrier;
  const addressKey = [
    address.phoneNumber,
    address.toAddressLine,
    address.toCity,
    address.toCountryCode,
    address.toName,
    address.toPostalCode,
    address.toStateProvinceCode,
    address.fromAddressLine,
    address.fromCity,
    address.fromCountryCode,
    address.fromName,
    address.fromPostalCode,
    address.fromStateProvinceCode,
    carrier?.carrierType,
    carrier?.accountType,
    carrier?.shipmentType,
  ].join('-');
  return addressKey || 'TBD';
};
